import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useState } from "react";
import { Building2, Users, Mail, HelpCircle, ChevronUp, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../../../utils";
import { SWIMLANE_LABEL_WIDTH } from "../constants";
import { SYSTEM_ICON } from "../../../../../constants/entity-icons";
import { appendDeletedSuffix, DELETED_ENTITY_CLASS } from "../../../../../utils/deleted-entity-utils";
import { resolveLaneTones } from "../laneColorTones";
import { useEditorMode } from "../../../flow-kit";
const laneIcons = {
    department: Building2,
    team: Users,
    system: SYSTEM_ICON,
    external: Mail,
    unassigned: HelpCircle
};
const ARROW_BUTTON_STYLE = {
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(0,0,0,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "auto",
    padding: 0
};
function ProcessFlowSwimlaneNode({ data }) {
    const { t } = useTranslation();
    const isEditor = useEditorMode();
    const [hovered, setHovered] = useState(false);
    if (!data) return null;
    const { label, laneType, isDeleted, width, height, onMoveUp, onMoveDown } = data;
    const Icon = laneIcons[laneType];
    const colors = resolveLaneTones(laneType, data.color);
    const displayLabel = appendDeletedSuffix(laneType === "unassigned" ? t("processFlow.swimlane.unassigned") : label, isDeleted, t);
    const isDashed = laneType === "system" || laneType === "external";
    const showArrows = isEditor && hovered;
    const renderArrow = (direction)=>{
        const handler = direction === "up" ? onMoveUp : onMoveDown;
        const ArrowIcon = direction === "up" ? ChevronUp : ChevronDown;
        const labelKey = direction === "up" ? "processFlowEditor.swimlane.moveUp" : "processFlowEditor.swimlane.moveDown";
        const enabled = !!handler;
        return /*#__PURE__*/ _jsx("button", {
            type: "button",
            "aria-label": t(labelKey),
            title: t(labelKey),
            disabled: !enabled,
            onClick: handler,
            style: {
                ...ARROW_BUTTON_STYLE,
                cursor: enabled ? "pointer" : "default",
                opacity: enabled ? 1 : 0.3
            },
            children: /*#__PURE__*/ _jsx(ArrowIcon, {
                size: 12,
                className: "text-fg-secondary"
            })
        });
    };
    return(// `overflow: visible` — while a child of this lane is dragged, the layout
    // keeps the RF node box at its pre-drag size (resizing the DOM box every
    // frame would fire RF's ResizeObserver and stutter the drag) and grows
    // only these data-driven SVG dimensions, so the rect must be able to
    // paint outside its box.
    /*#__PURE__*/ _jsxs("svg", {
        width: width,
        height: height,
        style: {
            overflow: "visible",
            pointerEvents: "none"
        },
        children: [
            /*#__PURE__*/ _jsx("rect", {
                x: 0,
                y: 0,
                width: width,
                height: height,
                rx: 8,
                ry: 8,
                fill: colors.bg,
                stroke: colors.border,
                strokeWidth: 1.5,
                strokeDasharray: isDashed ? "8 4" : undefined
            }),
            /*#__PURE__*/ _jsx("rect", {
                x: 0,
                y: 0,
                width: SWIMLANE_LABEL_WIDTH,
                height: height,
                rx: 8,
                ry: 8,
                fill: colors.labelBg
            }),
            /*#__PURE__*/ _jsx("rect", {
                x: SWIMLANE_LABEL_WIDTH - 8,
                y: 0,
                width: 8,
                height: height,
                fill: colors.labelBg
            }),
            /*#__PURE__*/ _jsx("line", {
                x1: SWIMLANE_LABEL_WIDTH,
                y1: 0,
                x2: SWIMLANE_LABEL_WIDTH,
                y2: height,
                stroke: colors.border,
                strokeWidth: 1.5,
                strokeDasharray: isDashed ? "8 4" : undefined
            }),
            /*#__PURE__*/ _jsx("foreignObject", {
                x: 0,
                y: 0,
                width: SWIMLANE_LABEL_WIDTH,
                height: height,
                children: /*#__PURE__*/ _jsxs("div", {
                    onMouseEnter: isEditor ? ()=>setHovered(true) : undefined,
                    onMouseLeave: isEditor ? ()=>setHovered(false) : undefined,
                    style: {
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        padding: "8px 4px",
                        pointerEvents: isEditor ? "auto" : "none"
                    },
                    children: [
                        showArrows && renderArrow("up"),
                        /*#__PURE__*/ _jsxs("div", {
                            className: cn("flex flex-col items-center gap-1.5", isDeleted && DELETED_ENTITY_CLASS),
                            children: [
                                /*#__PURE__*/ _jsx(Icon, {
                                    size: 16,
                                    className: "text-muted-foreground"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    style: {
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: "#374151",
                                        textAlign: "center",
                                        lineHeight: 1.2,
                                        wordBreak: "break-word",
                                        maxWidth: SWIMLANE_LABEL_WIDTH - 16
                                    },
                                    children: displayLabel
                                })
                            ]
                        }),
                        showArrows && renderArrow("down")
                    ]
                })
            })
        ]
    }));
}
export default /*#__PURE__*/ memo(ProcessFlowSwimlaneNode);

//# sourceMappingURL=SwimlaneNode.js.map